import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Switch, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { EVENTO_ABRIR, guardar, leer } from "../../domain/consentimiento/consentimiento";
import { VARIABLE_BARRA_INFERIOR } from "../../hooks/useBarraInferior";
import { BORDE, estiloSwitch, FUENTE, PRIMARIO, TEXTO_SECUNDARIO, TITULO } from "../ui/Formulario";

const POLITICA = "https://passgo.com.ar/legal";
const botonSx = { textTransform: "none", borderRadius: "8px" };
const pildoraSx = {
  textTransform: "none",
  borderRadius: "999px",
  px: 2,
  py: 0.5,
  fontSize: 13,
  whiteSpace: "nowrap",
  flex: { xs: 1, md: "none" },
};

const Opcion = ({ titulo, detalle, checked, disabled, onChange }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.75, "& + &": { borderTop: `1px solid ${BORDE}` } }}>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: TITULO }}>{titulo}</Typography>
      <Typography sx={{ fontSize: 13, color: TEXTO_SECUNDARIO }}>{detalle}</Typography>
    </Box>
    <Switch
      sx={estiloSwitch}
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
      inputProps={{ "aria-label": titulo }}
    />
  </Box>
);

const CookieConsent = () => {
  const [consentimiento, setConsentimiento] = useState(() => leer());
  const [preferencias, setPreferencias] = useState(false);
  const [elecciones, setElecciones] = useState({ publicidad: false, analitica: false });

  useEffect(() => {
    const abrir = () => {
      const actual = leer();
      setElecciones({ publicidad: Boolean(actual?.publicidad), analitica: Boolean(actual?.analitica) });
      setPreferencias(true);
    };
    window.addEventListener(EVENTO_ABRIR, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR, abrir);
  }, []);

  // en el login tapa los botones en mobile y ahí no hay nada que medir
  const enLogin = useLocation().pathname.startsWith("/login");

  const elegir = (valor) => {
    setConsentimiento(guardar(valor));
    setPreferencias(false);
  };

  return (
    <>
      {!consentimiento && !preferencias && !enLogin && (
        <Box
          role="region"
          aria-label="Aviso de cookies"
          sx={{
            position: "fixed",
            left: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            bottom: { xs: `calc(var(${VARIABLE_BARRA_INFERIOR}, 0px) + 8px)`, sm: `calc(var(${VARIABLE_BARRA_INFERIOR}, 0px) + 16px)` },
            mx: "auto",
            maxWidth: 1280,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            gap: { xs: 1.5, md: 3 },
            pl: { xs: 2.5, md: 3.5 },
            pr: { xs: 2.5, md: 1.5 },
            py: { xs: 2, md: 1.5 },
            bgcolor: "#fff",
            border: `1px solid ${BORDE}`,
            borderRadius: { xs: "16px", md: "999px" },
            boxShadow: "0px 8px 24px rgba(11, 0, 98, 0.10)",
            zIndex: 1150,
          }}
        >
          <Typography sx={{ flex: 1, fontSize: 14, color: TITULO, lineHeight: 1.5 }}>
            Usamos cookies para que PassGo funcione y, si aceptás, para que los organizadores midan sus anuncios.{" "}
            <Link href={POLITICA} target="_blank" rel="noopener noreferrer" underline="always" sx={{ color: TEXTO_SECUNDARIO }}>
              Política de privacidad
            </Link>
          </Typography>
          <Box sx={{ display: "flex", flexWrap: { xs: "wrap", md: "nowrap" }, alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Link
              component="button"
              type="button"
              underline="always"
              onClick={() => setPreferencias(true)}
              sx={{ fontSize: 13, color: TITULO, mr: { md: 1 }, order: { xs: 3, md: 0 }, width: { xs: "100%", md: "auto" }, textAlign: "center", mt: { xs: 0.5, md: 0 } }}
            >
              Configurar
            </Link>
            <Button
              onClick={() => elegir({})}
              sx={{ ...pildoraSx, color: TITULO, bgcolor: "#f4f4f5", "&:hover": { bgcolor: "#e4e4e7" } }}
            >
              Rechazar
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => elegir({ publicidad: true, analitica: true })}
              sx={{ ...pildoraSx, bgcolor: PRIMARIO }}
            >
              Aceptar todas
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={preferencias}
        onClose={() => setPreferencias(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ fontFamily: FUENTE, fontWeight: 700, color: TITULO, pb: 0.5 }}>Configurar cookies</DialogTitle>
        <DialogContent>
          <Opcion titulo="Esenciales" detalle="Para iniciar sesión, comprar y que el sitio funcione." checked disabled />
          <Opcion
            titulo="Publicidad"
            detalle="Para que el organizador mida sus anuncios, por ejemplo con el pixel de Meta."
            checked={elecciones.publicidad}
            onChange={(v) => setElecciones((e) => ({ ...e, publicidad: v }))}
          />
          <Opcion
            titulo="Analítica"
            detalle="Estadísticas de uso para mejorar PassGo."
            checked={elecciones.analitica}
            onChange={(v) => setElecciones((e) => ({ ...e, analitica: v }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPreferencias(false)} sx={{ ...botonSx, color: "#374151" }}>
            Cancelar
          </Button>
          <Button variant="contained" disableElevation onClick={() => elegir(elecciones)} sx={{ ...botonSx, bgcolor: PRIMARIO }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
